// Design and Video - job cards (step-by-step instructions), keyed by job code.
// A step is { text, tag }; a red STOP bar is { stop }. Loaded automatically by constants/jobCards.js.

export const division = "Design and Video";

export const cards = {
  V1: {
    title: "Reel — we shoot it",
    before: [
      "Brief with the client's chosen style",
      "Client brand files",
      "Full list of what to deliver",
    ],
    steps: [
      { text: "Write the script, and write what each shot will look like", tag: "Script" },
      { stop: "Division Lead or named project lead approves the script" },
      { text: "Sit with the shoot team and agree the final look", tag: "Everyone knows the plan" },
      { text: "Make the shot list", tag: "Shot list" },
      { text: "Book the gear now, not on shoot day", tag: "Gear confirmed" },
      { text: "Message client 48 hrs before: time, place, who is needed, what to wear", tag: "Message sent" },
      { text: "Shoot everything on the list + extra footage", tag: "Raw footage" },
      { text: "Test the sound and listen back before the first real take", tag: "Sound is clean" },
      { text: "Check the shot list BEFORE anyone packs up", tag: "Nothing missed" },
      { text: "Copy footage to 2 places the same day, before clearing the card", tag: "Footage is safe" },
      { text: "First cut — story working, nothing pretty yet", tag: "Video v1" },
      { stop: "Division Lead or named project lead approves the first cut" },
      { text: "Add b-roll, sound, music, graphics", tag: "Video v2" },
      { text: "Final edit: subtitles, colour, brand elements", tag: "Final video" },
      { text: "Run the check list before showing anyone", tag: "Checked" },
      { stop: "Client approves — show it on a call, do not just send it" },
      { text: "Make other sizes, thumbnail, captions", tag: "All files" },
      { text: "Hand to the person who publishes", tag: "Handed over" },
    ],
    finishedWhen: "All files are in the Delivered folder, the publishing person has them, and the project is archived.",
  },
  V2: {
    title: "Reel — client sends the footage",
    before: [
      "Footage received",
      "Brief with the client's style",
      "Full list of what to deliver",
    ],
    steps: [
      { text: "Check the footage FIRST: is it clear enough, is the sound usable, is there enough of it", tag: "Footage is usable" },
      { text: "If it is not usable, say so NOW — not after you have started editing", tag: "Problem raised" },
      { text: "First cut — story working, nothing pretty yet", tag: "Video v1" },
      { stop: "Division Lead or named project lead approves the first cut" },
      { text: "Final edit: subtitles, colour, sound, brand elements", tag: "Final video" },
      { text: "Run the check list", tag: "Checked" },
      { stop: "Client approves" },
      { text: "Other sizes, thumbnail, captions, then hand over", tag: "All files" },
    ],
    finishedWhen: "All files in the Delivered folder and handed to the publishing person.",
  },
  V3: {
    title: "Long video (podcast, interview)",
    before: [
      "Footage received and checked",
      "Client word list updated for this episode",
      "Full list of what to deliver",
    ],
    steps: [
      { text: "Update the client word list: guest names, titles, difficult words", tag: "Word list ready" },
      { text: "Write down everything you have to deliver: main video, short clips, audiogram, thumbnail, chapters, transcript", tag: "Delivery list" },
      { text: "First cut: sync everything, cut dead air and unusable bits", tag: "Video v1" },
      { stop: "Client approves the first cut and says what to remove" },
      { text: "Clean the audio: levels, noise", tag: "Clean audio" },
      { text: "Get the transcript and find b-roll", tag: "Transcript + b-roll" },
      { stop: "Client approves one sample of the b-roll and graphics look" },
      { text: "Final edit", tag: "Final video" },
      { text: "Check names and words against the client word list", tag: "Names correct" },
      { stop: "Client approves" },
      { text: "Make short clips from moments you marked while editing", tag: "Short clips" },
      { text: "Audiogram, thumbnail, chapters, transcript", tag: "All extras" },
    ],
    finishedWhen: "Main video plus every extra on the delivery list is handed over.",
  },
  V4: {
    title: "Short clips from a long video",
    before: [
      "Long video already approved",
      "Timecodes you marked while editing it",
    ],
    steps: [
      { text: "While editing the long video, note the timecode of every strong moment", tag: "Moment list" },
      { text: "Agree with the social team which clips to make", tag: "Clip list" },
      { text: "Cut each clip so it works alone: hook in first 2 seconds, one idea, clean ending", tag: "Clip cut" },
      { text: "Reframe to vertical, keep the speaker in the middle", tag: "Vertical clip" },
      { text: "Burn in the captions — most people watch on mute", tag: "Captions done" },
      { text: "Add the brand elements from the motion pack", tag: "Branded clip" },
      { text: "Division Lead checks, then hand to the publishing person", tag: "Handed over" },
    ],
    finishedWhen: "Every agreed clip is cut, captioned, branded, and handed over.",
  },
  V5: {
    title: "Founder / talking head video",
    before: [
      "Topic list agreed",
      "Client available on the day",
    ],
    steps: [
      { text: "Agree 6 to 10 topics for ONE shoot day. Setup takes the time, not the talking", tag: "Topic list" },
      { text: "Give talking points, not a full script — people read scripts badly", tag: "Talking points" },
      { text: "Send a note before: what to wear, where to look, no fine patterns or logos", tag: "Note sent" },
      { text: "Set up framing, lights, mic, background", tag: "Setup ready" },
      { text: "Record sound and LISTEN BACK before the first real take", tag: "Sound is clean" },
      { text: "Shoot — more than one take per topic", tag: "Raw footage" },
      { text: "Copy footage to 2 places the same day", tag: "Footage safe" },
      { text: "Edit each video: trim, captions, brand elements, b-roll", tag: "Final videos" },
      { stop: "Division Lead or named project lead checks, then client approves" },
    ],
    finishedWhen: "Every video from the shoot day is edited, checked, and approved.",
  },
  V6: {
    title: "Customer story video",
    before: [
      "Customer has agreed",
      "Agreed where the video will be used",
    ],
    steps: [
      { text: "Confirm at booking where the video will be used — including paid ads", tag: "Usage agreed" },
      { text: "Send the questions ahead so they come prepared", tag: "Questions sent" },
      { text: "Shoot the interview + b-roll of their place or the product", tag: "Raw footage" },
      { text: "Copy footage to 2 places the same day", tag: "Footage safe" },
      { text: "Edit: story order, b-roll, captions, brand elements", tag: "Final video" },
      { text: "Run the check list", tag: "Checked" },
      { stop: "Client approves AND the customer approves" },
    ],
    finishedWhen: "Both the client and the customer have approved, in writing.",
  },
  V7: {
    title: "Explainer / product video",
    before: [
      "Clear topic and audience",
      "What the viewer should do after watching",
    ],
    steps: [
      { text: "Write the script and time it — about 140 words per minute of video", tag: "Timed script" },
      { stop: "Client approves the script" },
      { text: "Make still pictures showing how the animation will look", tag: "Style frames" },
      { stop: "Client approves the look — NEVER animate before this" },
      { text: "Record the voiceover or finalise the on-screen text", tag: "Audio / text" },
      { text: "Animate", tag: "Animation" },
      { text: "Add sound and music", tag: "Full video" },
      { stop: "Check list, then client approves" },
    ],
    finishedWhen: "Final video approved and delivered in the required formats.",
  },
  V8: {
    title: "Video ads for paid campaigns",
    before: [
      "Brief from the paid team, at least 5 working days before launch",
      "Sizes, lengths, how many versions",
    ],
    steps: [
      { text: "Read the brief: what are they testing, which sizes, how many versions, launch date", tag: "Brief understood" },
      { text: "Make ONE main video first", tag: "Main video" },
      { stop: "Division Lead or named project lead approves the main video" },
      { text: "Make the versions from the approved main video", tag: "Versions" },
      { text: "Change only ONE thing per version — usually the first 3 seconds", tag: "Clean test" },
      { text: "Check each size works and reads without sound", tag: "Sizes checked" },
      { text: "Deliver to the paid team 3 working days before launch", tag: "Delivered" },
    ],
    finishedWhen: "All versions delivered, correctly named, 3 days before launch.",
  },
  V9: {
    title: "Event coverage",
    before: [
      "Event run sheet",
      "List of moments and people that must be captured",
    ],
    steps: [
      { text: "Get the run sheet and agree the must-capture list", tag: "Must-capture list" },
      { text: "Visit the place or go through the run sheet before the day — a live event has no second take", tag: "Plan ready" },
      { text: "Shoot to the run sheet", tag: "Raw footage" },
      { text: "Copy footage to 2 places the same day", tag: "Footage safe" },
      { text: "Send best shots within 48 hrs if they need to post quickly", tag: "Quick selects" },
      { text: "Edit the highlight video and social clips", tag: "Final videos" },
      { stop: "Check list, then client approves" },
    ],
    finishedWhen: "Highlight video and social clips approved and delivered.",
  },
  D1: {
    title: "Motion graphics pack (once per brand)",
    before: [
      "Brand guidelines with the video rules",
    ],
    steps: [
      { text: "List what goes in the pack: intro, outro, name titles, transitions, subtitle style, end card, logo animation", tag: "Pack list" },
      { text: "Make still pictures of each element", tag: "Style frames" },
      { stop: "Project lead (if named), then Division Lead, then founders approve the look" },
      { text: "Build the animated elements", tag: "Elements ready" },
      { text: "Save as templates any editor can use without rebuilding", tag: "Templates" },
      { text: "Show the edit team how to use them", tag: "Team trained" },
    ],
    finishedWhen: "Templates are saved and every editor knows how to apply them.",
  },
  D2: {
    title: "Photo shoot (corporate / brand)",
    before: [
      "Shot list agreed",
      "Agreed how much retouching is needed",
    ],
    steps: [
      { text: "Agree the shot list AND the retouching level at brief stage", tag: "Shot list" },
      { text: "Plan: place, lights, gear, schedule, who is in the photos", tag: "Shoot plan" },
      { text: "Shoot", tag: "Raw photos" },
      { text: "Copy photos to 2 places the same day", tag: "Photos safe" },
      { text: "Pick the best ones and send those FIRST — do not retouch everything", tag: "Selects" },
      { stop: "Client picks from the selects" },
      { text: "Retouch only what they picked, export, deliver", tag: "Final photos" },
    ],
    finishedWhen: "Only the chosen photos are retouched and delivered.",
  },
  D3: {
    title: "Small deliveries: thumbnails, captions, sizes, audiograms",
    before: [
      "Main video already approved",
      "List of what was promised",
    ],
    steps: [
      { text: "Thumbnail: readable when small, face or subject clear, under 5 words", tag: "Thumbnail" },
      { text: "Captions: check names and words against the client word list", tag: "Captions + SRT" },
      { text: "Extra sizes: reframe, do not just crop. Keep the subject in place", tag: "All sizes" },
      { text: "Audiogram: waveform or simple visual, captions burnt in", tag: "Audiogram" },
      { text: "Deliver these WITH the main video, not after someone asks", tag: "All delivered" },
    ],
    finishedWhen: "Everything on the promised list is delivered together with the main video.",
  },
  F1: {
    title: "Client setup — do this BEFORE any work",
    before: [
      "A new client, or a client where something below is missing",
    ],
    steps: [
      { text: "Check the client has: logo files, colours, fonts", tag: "Brand files" },
      { text: "Check they have brand guidelines that cover VIDEO too, not just posts", tag: "Guidelines" },
      { text: "Check a video style has been chosen for this client and written down", tag: "Style locked" },
      { text: "Check there is a word list: their terms, product names, how to say difficult words", tag: "Word list" },
      { text: "Check their old photos, footage and logos are collected in one folder", tag: "Asset folder" },
      { text: "ONE named person at the client approves our work, and we know the ONE channel where their approvals are written down", tag: "Approver named" },
      { text: "The client's dos and don'ts are written down: what we must never say, show or claim", tag: "Dos and don'ts" },
      { text: "Their tone of voice is written down. If it does not exist, writing it becomes a job", tag: "Tone of voice" },
      { text: "IF ANYTHING IS MISSING: tell the Division Lead. It becomes a job with a date. Do not work around it", tag: "Raised" },
    ],
    finishedWhen: "All five exist and are saved in the client's Foundation folder.",
  },
  M1: {
    title: "Build a brand identity",
    before: [
      "Client has told us who they are, who they sell to, and who they compete with",
      "One named person on the client side who can say yes",
    ],
    steps: [
      { text: "Ask the client about their positioning, audience, competitors, and what must not change", tag: "Discovery notes" },
      { text: "Make mood boards. THREE directions maximum", tag: "3 directions" },
      { stop: "Client picks ONE direction, in writing" },
      { text: "Build the logo: main, secondary, and the small mark", tag: "Logo files" },
      { text: "Build the colour set and check it is readable", tag: "Colours" },
      { text: "Pick the fonts and the sizes", tag: "Fonts" },
      { text: "Show how it looks on a post, a video end card, a document, a sign", tag: "Examples" },
      { stop: "Project lead (if named), then Division Lead, then founders check" },
      { stop: "Client approves" },
      { text: "Send every file type they will need", tag: "All files" },
    ],
    finishedWhen: "Every file is delivered and saved in the client's Foundation folder.",
  },
  M2: {
    title: "Write the brand guidelines",
    before: [
      "Brand identity approved, or the client already has one",
    ],
    steps: [
      { text: "Logo rules: versions, space around it, smallest size, what NOT to do", tag: "Logo rules" },
      { text: "Colour and font rules", tag: "Colour + font rules" },
      { text: "Picture rules: photos, illustrations, is AI allowed and where", tag: "Picture rules" },
      { text: "VIDEO rules: titles, name strips, transitions, end card, subtitle style, safe edges", tag: "Video rules" },
      { text: "Sound rules: what kind of music, which library", tag: "Sound rules" },
      { text: "Do and do-not examples with pictures", tag: "Examples" },
      { stop: "Project lead (if named), then Division Lead, then founders check. Then the client approves" },
    ],
    finishedWhen: "Guidelines PDF and editable file saved. Every brief points to it.",
  },
  M3: {
    title: "Lock the video style for a client",
    before: [
      "Our house style library exists",
      "Brand guidelines are in place",
    ],
    steps: [
      { text: "Play the client our 6 to 8 house styles with example clips", tag: "Styles shown" },
      { stop: "Client picks one main style" },
      { text: "Write down the details: speed, cuts, graphics, text, colour, sound, captions, music, mood", tag: "Style notes" },
      { text: "Ask for 2 or 3 videos they LIKE and exactly what they like about each", tag: "Likes" },
      { text: "Ask for 2 or 3 videos they DISLIKE and why. This is more useful", tag: "Dislikes" },
      { text: "Write it all down and get it confirmed", tag: "Signed style" },
    ],
    finishedWhen: "The style is written down and saved. Every brief for this client points to it.",
  },
  M4: {
    title: "Build the client word list",
    before: [
      "Someone at the client we can ask",
    ],
    steps: [
      { text: "Collect their industry words, product names, short forms", tag: "Word list" },
      { text: "Collect people's names with correct spelling and job titles", tag: "Names" },
      { text: "Write how to SAY the hard words", tag: "Pronunciation" },
      { text: "Write the words they do not want used", tag: "Avoid list" },
      { text: "RULE: any editor who has to look up a word adds it the same day", tag: "Living list" },
    ],
    finishedWhen: "The list is saved in the Foundation folder and every brief links to it.",
  },
  M5: {
    title: "Build the client asset folder",
    before: [
      "Client has shared their old files",
    ],
    steps: [
      { text: "Collect old brand files, product photos, old footage, logos", tag: "Files collected" },
      { text: "Put them in the standard folders with clear names", tag: "Organised folder" },
      { text: "Write down where anything borrowed came from and what we may use it for", tag: "Source list" },
      { text: "Save the permission forms from anyone filmed or photographed", tag: "Permissions" },
      { text: "List what is MISSING and tell the Division Lead", tag: "Gap list" },
    ],
    finishedWhen: "Folder is organised, sources are noted, and the gap list is with the Division Lead.",
  },
  M6: {
    title: "Standalone animation (15 to 30 sec)",
    before: [
      "Brief and brand guidelines",
    ],
    steps: [
      { text: "Write the script", tag: "Script" },
      { stop: "Client approves the script" },
      { text: "Make still pictures of the look", tag: "Style frames" },
      { stop: "Client approves the look. NEVER animate before this" },
      { text: "Animate", tag: "Animation" },
      { text: "Add sound, check, client approves", tag: "Final" },
    ],
    finishedWhen: "Final animation approved and delivered.",
  },
  M7: {
    title: "Illustrations and icons",
    before: [
      "Style agreed and brand guidelines in place",
      "How many are needed",
    ],
    steps: [
      { text: "Agree the style and how many are needed", tag: "Set list" },
      { text: "Make ONE as the example", tag: "One item" },
      { stop: "Division Lead or named project lead approves that ONE before the rest is made" },
      { text: "Make the rest to match", tag: "Full set" },
      { text: "Export in the needed formats and file them", tag: "Files" },
    ],
    finishedWhen: "Full set delivered in all required formats.",
  },
  M8: {
    title: "Master templates (deck, doc)",
    before: [
      "Brand guidelines approved",
      "We know which software the client actually uses",
    ],
    steps: [
      { text: "Ask which software they actually use. A beautiful template in software they do not have is useless", tag: "Software known" },
      { text: "Design the master layouts", tag: "Layouts" },
      { text: "Build them as templates they can edit, with the fixed parts locked", tag: "Templates" },
      { stop: "Client approves" },
      { text: "Deliver with a one page note on how to use them", tag: "Delivered" },
    ],
    finishedWhen: "Editable templates delivered with a short usage note.",
  },
};
